import {
    type RequestTransaction,
    RequestStatus,
} from '@features/requests/types';
import type { EmployeeMessage } from '@shared/types';

/**
 * Map RequestTransaction[] to EmployeeMessage[].
 * Translates comment → message, registered → Date if string, defaults priority to 0.
 */
export function toEmployeeMessages(
    transactions: RequestTransaction[] | undefined,
): EmployeeMessage[] {
    if (!transactions) {
        return [];
    }

    return transactions.map((t): EmployeeMessage => ({
        id: t.id,
        message: t.comment ?? '',
        registered:
            t.registered instanceof Date
                ? t.registered
                : t.registered
                  ? new Date(t.registered)
                  : undefined,
        status: t.status,
        priority: 0,
    }));
}

/**
 * Status label map for employee messages.
 */
const statusLabelMap: Record<string, string> = {
    CREATED: 'Opprettet',
    COMMENTED: 'Kommentar',
    COMMENTED_BY_LEADER: 'Kommentar fra leder',
    SUBMITTED: 'Innsendt',
    APPROVED: 'Godkjent',
    REJECTED: 'Avslått',
    MODIFIED: 'Endret',
};

/**
 * Returns a human-readable status label, falling back to the raw status or ''.
 */
export function getMessageStatusLabel(status?: string): string {
    if (!status) {
        return '';
    }
    return statusLabelMap[status] ?? status;
}

/**
 * Returns a UI variant based on status.
 */
export function getMessageStatusVariant(
    status?: string,
): 'info' | 'success' | 'error' {
    if (status === RequestStatus.APPROVED) {
        return 'success';
    }
    if (status === RequestStatus.REJECTED) {
        return 'error';
    }
    return 'info';
}

/**
 * Formats a date using Norwegian locale.
 * Returns '' when no date is provided.
 */
export function formatMessageDate(date?: Date): string {
    if (!date) {
        return '';
    }
    return date.toLocaleString('nb-NO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
